import React, { useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

import {
  AdyenCheckout,
  SubmitData,
  PayPal,
  PayPalConfiguration,
  AddressData,
  UIElement,
  SubmitActions,
  PaymentCompletedData,
  AdyenCheckoutError,
  AdditionalDetailsData,
  AdditionalDetailsActions,
  PaymentFailedData,
  CoreConfiguration,
} from '@adyen/adyen-web';
import styled from '@emotion/styled';

import { ADYEN_CLIENT_KEY } from '../../api/constants';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/shared/store/rootReducer';
import paths from '../../routing/paths';
import { useNavigate } from 'react-router-dom';
import { PaypalECSInformations } from './PaypalECSInformations';
import { breakpoints, colors, opacities } from '../../../../shared/design-system';
import { openModalNew } from '../../common/actions';
import { durations } from '../../common/constants';
import { validateMainlandFrancePostalCode } from '../../cart/utils';
import { setOrderSuccessData, updatePaypalECS } from '../../cart/actions';
import { getBFFData, Mutations } from '../../api';
import { getClientID } from '../../tracking';
import { ERR_GENERIC } from '../../common/locale';
import { orderSuccessDataInitialState, paypalECSInitialState } from '../../cart/state';

const StyledPaypalECSWrapper = styled.div`
  display: flex;
  flex-wrap: no-wrap;
  width: calc(100% - 16px);
  margin-top: 8px;
  gap: 8px;
  max-height: 44px;

  @media (min-width: ${breakpoints.S + 100}px) {
    width: 100%;
  }

  @media (min-width: ${breakpoints.M}px) {
    width: calc(100% - 16px);
  }

  .paypal-ecs {
    height: 44px;
    width: calc(100% - 36px);
    max-width: 750px;
    flex-shrink: 0;
  }

  .paypal-ecs-btn-open-informations-modal {
    flex-grow: 1;
    border: none;
    border-radius: 8px;
    background-color: ${colors.PAYPAL_YELLOW};
    min-width: 44px;
    height: 44px;
    transition: opacity ${durations.ENTER}ms ease-in-out;

    @media (hover: hover) {
      :hover {
        cursor: pointer;
        opacity: ${opacities.HOVERED};
      }
    }
  }

  .adyen-checkout__paypal__status,
  .adyen-checkout__paypal__status--processing {
    padding: 0;
    height: 44px;
  }
`;

export const PaypalECS = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { total } = useSelector((state: RootState) => state.cart.cart);
  const { orderSuccessData } = useSelector((state: RootState) => state.cart);
  const gaClientId = getClientID();

  const getAdyenConfiguration = (): CoreConfiguration => {
    const environment: 'live' | 'test' =
      process.env.DEPLOYMENT_ENV === 'production' ? 'live' : 'test';
    return {
      locale: 'fr-FR',
      countryCode: 'FR',
      environment,
      clientKey: ADYEN_CLIENT_KEY,
      translations: {
        'fr-FR': {
          'creditCard.cvcField.placeholder': '1234',
        },
      },
      amount: { value: parseInt((total * 100).toFixed(0), 10), currency: 'EUR' },
    };
  };

  const getPaypalConfiguration = (): PayPalConfiguration => {
    return {
      isExpress: true,
      countryCode: 'FR',
      userAction: 'continue',
      style: {
        height: 44,
      },
      onShippingAddressChange: async (
        data: any,
        actions: {
          reject: (reason?: string) => Promise<void>;
        },
        component: any
      ) => {
        const currentPaymentData = component.paymentData;
        const { countryCode, postalCode, city } = data.shippingAddress;

        if (countryCode !== 'FR') {
          return actions.reject(data.errors.COUNTRY_ERROR);
        }
        if (validateMainlandFrancePostalCode(postalCode)) {
          return actions.reject(data.errors.ZIP_ERROR);
        }

        const response = await getBFFData(Mutations.paypalUpdateOrder, {
          countryCode,
          postalCode,
          city,
          paymentData: currentPaymentData,
        });
        const { paymentData, cart, statusCode, errorType } = response.data.paypalUpdateOrder;
        if (response.ok && paymentData && cart && statusCode === 200 && !errorType) {
          dispatch(updatePaypalECS({ cart }));
          component.updatePaymentData(paymentData);
        } else {
          return actions.reject(errorType ?? ERR_GENERIC);
        }
        return undefined;
      },
      onInit: (data: any, actions: any) => {
        const { correlationID } = data;
        if (correlationID) {
          actions.enable();
        } else {
          actions.disable();
        }
      },
      onClick: () => {
        if (orderSuccessData.cart.items.length > 0) {
          dispatch(setOrderSuccessData(orderSuccessDataInitialState));
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'start_checkout' });
      },
      onSubmit: async (state: SubmitData, component: UIElement, actions: SubmitActions) => {
        const credentials = await fetchAuthSession();
        const initialCognitoId = credentials.identityId;
        if (initialCognitoId) {
          dispatch(updatePaypalECS({ cognito: { initialCognitoId } }));
        }
        const { data } = state;
        try {
          const payload = JSON.stringify({
            returnUrl: `${window.location.origin}${paths.CART}`,
            channel: 'web',
            origin: window.location.origin,
            browserInfo: {
              colorDepth: window.screen.colorDepth,
              screenHeight: window.screen.height,
              screenWidth: window.screen.width,
              timeZoneOffset: new Date().getTimezoneOffset(),
              javaEnabled: window.navigator.javaEnabled(),
              language: window.navigator.language,
            },
            ...data,
          });
          const response = await getBFFData(Mutations.paypalPayments, { payload, gaClientId });
          const { action, resultCode, statusCode, errorType } = response.data.paypalPayments;
          if (response.ok && action && resultCode && statusCode === 200 && !errorType) {
            component.handleAction(JSON.parse(action));
          } else {
            actions.reject(errorType ?? ERR_GENERIC);
          }
        } catch (error) {
          actions.reject(error ?? ERR_GENERIC);
        }
      },
      onAdditionalDetails: async (
        state: AdditionalDetailsData,
        _component: UIElement,
        _actions: AdditionalDetailsActions
      ) => {
        const { data } = state;
        dispatch(updatePaypalECS({ details: data }));
        navigate(paths.PPECS_REVIEWS_PAGE);
      },
      onAuthorized: (
        data: {
          authorizedEvent: any;
          billingAddress?: Partial<AddressData>;
          deliveryAddress?: Partial<AddressData>;
        },
        actions: {
          resolve: () => void;
          reject: () => void;
        }
      ) => {
        const { status } = data.authorizedEvent;

        if (status === 'APPROVED' && data.deliveryAddress?.country === 'FR') {
          const billingAddress = {
            houseNumberOrName: data.billingAddress?.houseNumberOrName ?? '',
            street: data.billingAddress?.street ?? '',
            postalCode: data.billingAddress?.postalCode ?? '',
            city: data.billingAddress?.city ?? '',
            country: data.billingAddress?.country ?? '',
            stateOrProvince: data.billingAddress?.stateOrProvince ?? '',
            firstName: data.billingAddress?.firstName ?? '',
            lastName: data.billingAddress?.lastName ?? '',
          };

          const deliveryAddress = {
            houseNumberOrName: data.deliveryAddress?.houseNumberOrName ?? '',
            street: data.deliveryAddress?.street ?? '',
            postalCode: data.deliveryAddress?.postalCode ?? '',
            city: data.deliveryAddress?.city ?? '',
            country: data.deliveryAddress?.country ?? '',
            stateOrProvince: data.deliveryAddress?.stateOrProvince ?? '',
            firstName: data.deliveryAddress?.firstName ?? '',
            lastName: data.deliveryAddress?.lastName ?? '',
          };

          const ecsData = {
            authorizedEvent: data.authorizedEvent,
            billingAddress,
            deliveryAddress,
          };

          dispatch(updatePaypalECS({ data: ecsData }));
          actions.resolve();
        } else {
          actions.reject();
        }
      },
      onPaymentCompleted: (_data: PaymentCompletedData, component: UIElement) => {
        component.setStatus('success');
      },
      onPaymentFailed: (_data: PaymentFailedData, component: UIElement) => {
        component.setStatus('error');
      },
      onError: (error: AdyenCheckoutError, component: UIElement) => {
        if (error) {
          dispatch(updatePaypalECS(paypalECSInitialState));
          component.setStatus('error');
        }
      },
      blockPayPalCreditButton: true,
      blockPayPalPayLaterButton: true,
    };
  };

  const handlePapypalModal = () => {
    dispatch(
      openModalNew({
        content: <PaypalECSInformations />,
        preset: 'inheritWidth',
      })
    );
  };

  useEffect(() => {
    const initializeCheckout = async () => {
      const checkout = await AdyenCheckout(getAdyenConfiguration());
      new PayPal(checkout, getPaypalConfiguration()).mount('#paypal-ecs');
    };
    initializeCheckout();
  }, []);

  return (
    <StyledPaypalECSWrapper>
      <div className="paypal-ecs" id="paypal-ecs" />
      <button
        className="paypal-ecs-btn-open-informations-modal"
        data-testid="paypal-express-btn-open-informations-modal"
        onClick={handlePapypalModal}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_628_1369)">
            <path
              d="M9.08984 18C4.08984 18 0.0898438 14 0.0898438 9C0.0898438 4 4.08984 0 9.08984 0C14.0898 0 18.0898 4 18.0898 9C18.0898 14 14.0898 18 9.08984 18ZM9.08984 17C13.4898 17 17.0898 13.4 17.0898 9C17.0898 4.6 13.4898 1 9.08984 1C4.68984 1 1.08984 4.6 1.08984 9C1.08984 13.4 4.68984 17 9.08984 17ZM8.68984 5.2V4.1H9.78984V5.2H8.68984ZM6.68984 7.3V6.5H9.78984V12H11.8898V12.8H6.58984V12H8.78984V7.3H6.68984Z"
              fill="black"
            />
          </g>
        </svg>
      </button>
    </StyledPaypalECSWrapper>
  );
};
